import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet"; // Import Helmet for meta tags
import { Link } from "react-router-dom";
import Header from "../layout/header4";
import Footer from "../layout/footer";
import { useTranslation } from "react-i18next";

const bg = require("../../images/banner/banner.jpg");

const Products = () => {
  const { t } = useTranslation();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [selectedSubCategory, setSelectedSubCategory] = useState("all");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(8);

  // Image loading state
  const [loadedImages, setLoadedImages] = useState({});

  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const response = await fetch(`${apiUrl}/subcategory/metallic`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setSubCategories(data);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    const fetchData = async () => {
      try {
        const response = await fetch(`${apiUrl}/product/metallique`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        console.log(data);
        const modifiedData = data.map((item) => {
          const { _id, ...rest } = item;
          return { id: _id, ...rest };
        });

        setProducts(modifiedData);
        setFilteredProducts(modifiedData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSubCategories();
    fetchData();
  }, []);

  // Filter products based on selected category and subcategory
  useEffect(() => {
    const fetchSubCategories = async () => {
      try {
        const response = await fetch(`${apiUrl}/subcategory`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        });

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        setSubCategories(data);
      } catch (error) {
        setError(error.message);
      }
    };

    fetchSubCategories();
  }, []);

  // Filter products based only on subCategory
  useEffect(() => {
    let filtered = products;

    if (selectedSubCategory !== "all") {
      filtered = filtered.filter(
        (product) => product.subCategory._id === selectedSubCategory
      );
    }

    setFilteredProducts(filtered);
    setCurrentPage(1);
  }, [selectedSubCategory, products]);

  const handleSubCategoryChange = (event) => {
    setSelectedSubCategory(event.target.value);
  };

  // Handle image load
  const handleImageLoad = (productId) => {
    setLoadedImages((prev) => ({
      ...prev,
      [productId]: true,
    }));
  };

  // Pagination logic
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredProducts.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (error) return <div>Error: {error}</div>;

  return (
    <>
      <Helmet>
        <title>{t("products.meta.title")}</title>
        <meta name="description" content={t("products.meta.description")} />
        <meta name="keywords" content={t("products.meta.keywords")} />
        <meta name="author" content="Gargouri emballages" />
        <meta property="og:title" content={t("products.meta.title")} />
        <meta
          property="og:description"
          content={t("products.meta.description")}
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.gargouri-emballages.com/products"
        />
        <meta
          property="og:image"
          content="https://backend.gargouri-emballages.com/upload/products/images/img-Coc 160-1746491128387-x5iak6.jpg"
        />
      </Helmet>

      <Header />
      <div className="page-content bg-white">
        <div
          className="dlab-bnr-inr overlay-black-middle bg-pt"
          style={{ backgroundImage: `url(${bg})` }}
        >
          <div className="container">
            <div className="dlab-bnr-inr-entry">
              <h1 className="text-white">{t("products.title")}</h1>
              <div className="breadcrumb-row">
                <ul className="list-inline">
                  <li>
                    <Link to="/">{t("header.home")}</Link>
                  </li>
                  <li>{t("header.plastics")}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="category-dropdown bg-light p-3">
          <div className="container">
            <select
              className="custom-select d-block w-50"
              onChange={handleSubCategoryChange}
              value={selectedSubCategory}
            >
              <option value="all">{t("products.allSubCategories")}</option>
              {subCategories?.map((subCategory) => (
                <option key={subCategory._id} value={subCategory._id}>
                  {subCategory.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="section-full content-inner bg-light ">
          <div className="container">
            <div className="row">
              {currentProducts.length > 0 ? (
                currentProducts.map((product) => (
                  <div
                    key={product.id}
                    className="col-lg-3 col-md-4 col-sm-6 mb-4"
                  >
                    <Link to={`/product-details/${product.id}`}>
                      <div className="item-box m-b10">
                        <div
                          className="item-img"
                          style={{ position: "relative" }}
                        >
                          {!loadedImages[product.id] && (
                            <div
                              style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                backgroundColor: "#f5f5f5",
                              }}
                            >
                              <div
                                className="spinner-border text-primary"
                                role="status"
                              >
                                <span className="sr-only">Loading...</span>
                              </div>
                            </div>
                          )}
                          <img
                            src={
                              process.env.REACT_APP_API_URL +
                                product.images?.[0] || "placeholder.jpg"
                            }
                            alt={product.name}
                            style={{
                              display: loadedImages[product.id]
                                ? "block"
                                : "block",
                              opacity: loadedImages[product.id] ? 1 : 0,
                              transition: "opacity 0.3s ease-in-out",
                            }}
                            onLoad={() => handleImageLoad(product.id)}
                          />
                          <div className="item-info-in">
                            <ul>
                              <li>
                                <Link to={`/product-details/${product?.id}`}>
                                  <i className="ti-eye"></i>
                                </Link>
                              </li>
                            </ul>
                          </div>
                        </div>
                        <div className="item-info text-center text-black p-a10">
                          <h6 className="item-title font-weight-500">
                            <Link to={`/product-details/${product.id}`}>
                              {product.name}
                            </Link>
                          </h6>
                          <h4 className="item-price">
                            <span className="text-primary">
                              {product.reference}
                            </span>
                          </h4>
                        </div>
                      </div>
                    </Link>
                  </div>
                ))
              ) : (
                <div className="col-12">
                  <p>{t("products.noProducts")}</p>
                </div>
              )}
            </div>

            {filteredProducts.length > 0 && (
              <div className="pagination-bx rounded-sm gray clearfix text-center">
                <ul className="pagination">
                  <li
                    className={`previous ${
                      currentPage === 1 ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage > 1) paginate(currentPage - 1);
                      }}
                    >
                      <i className="ti-arrow-left"></i> {t("pagination.prev")}
                    </a>
                  </li>

                  {[...Array(totalPages).keys()].map((number) => (
                    <li
                      key={number + 1}
                      className={currentPage === number + 1 ? "active" : ""}
                    >
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          paginate(number + 1);
                        }}
                      >
                        {number + 1}
                      </a>
                    </li>
                  ))}

                  <li
                    className={`next ${
                      currentPage === totalPages ? "disabled" : ""
                    }`}
                  >
                    <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        if (currentPage < totalPages) paginate(currentPage + 1);
                      }}
                    >
                      {t("pagination.next")} <i className="ti-arrow-right"></i>
                    </a>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer hideContactInfo />
    </>
  );
};

export default Products;
